import { useRouter } from "next/router";
import Link from "next/link";
import { ReactNode, AnchorHTMLAttributes, useMemo } from "react";
import React from "react";

interface CustomLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
  children: ReactNode;
}

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];

function getCurrentLocation(asPath: string) {
  const withoutHash = (asPath || "/").split("#")[0];
  const [path, search = ""] = withoutHash.split("?");
  return {
    path: path || "/",
    searchParams: new URLSearchParams(search),
  };
}

const CustomLink: React.FC<CustomLinkProps> = ({
  href,
  children,
  ...props
}) => {
  const router = useRouter();

  const { combinedHref, isHashLink } = useMemo(() => {
    const [baseURL, hashFragment] = href.split("#");
    const [rawPath, newQueryString] = baseURL.split("?");
    const { path: currentPath, searchParams } = getCurrentLocation(
      router.asPath
    );

    // Âncoras (#proposta) não têm caminho. Sem o path real, o Next
    // resolve a query contra o padrão da rota (/[carro]).
    const isHashOnly = rawPath.length === 0;
    const finalParams = new URLSearchParams();

    if (isHashOnly) {
      searchParams.forEach((value, key) => {
        finalParams.set(key, value);
      });
    } else {
      UTM_KEYS.forEach((key) => {
        const value = searchParams.get(key);
        if (value) finalParams.set(key, value);
      });
    }

    new URLSearchParams(newQueryString || "").forEach((value, key) => {
      finalParams.set(key, value);
    });

    const resolvedPath = isHashOnly ? currentPath : rawPath;
    const queryStringFinal = finalParams.toString();
    let hrefWithParams = queryStringFinal
      ? `${resolvedPath}?${queryStringFinal}`
      : resolvedPath;

    if (hashFragment) {
      hrefWithParams += `#${hashFragment}`;
    }

    return { combinedHref: hrefWithParams, isHashLink: isHashOnly };
  }, [href, router.asPath]);

  if (isHashLink) {
    return (
      <a href={combinedHref} {...props}>
        {children}
      </a>
    );
  }

  return (
    <Link href={combinedHref} {...props}>
      {children}
    </Link>
  );
};

export default React.memo(CustomLink);
